"use client";

import { useEffect, useRef, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const imageRef = useRef<HTMLDivElement | null>(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const node = imageRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const distance = rect.top + rect.height / 2 - viewportCenter;
      setOffset(distance * -0.06);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const tickerItems = ["ECO-FRIENDLY", "LAND CLEARING", "SOIL ENRICHMENT", "ECO-FRIENDLY", "LAND CLEARING", "SOIL ENRICHMENT"];
  return <section data-section-id="1931" id="hero" className="relative overflow-hidden bg-background text-foreground py-28 md:py-40 px-5 md:px-10">
      <div className="mx-auto max-w-[1320px]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          {}
          <div ref={ref} className="lg:col-span-7 order-2 lg:order-1 relative">
            {}
            <span aria-hidden="true" className="pointer-events-none absolute -top-16 -left-2 select-none font-default font-bold leading-none text-secondary/[0.06] text-[14rem] md:text-[20rem]">
              01
            </span>

            <div className="relative">
              {}
              <p className={["font-mono uppercase text-[0.72rem] md:text-[0.82rem] tracking-[0.22em] text-muted-foreground transition-all duration-[600ms] ease-[cubic-bezier(0.16,1,0.3,1)]", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"].join(" ")}>
                {"\u00A7"} 01 / LAND MANAGEMENT EXPERTS
              </p>

              {}
              <h1 className={["mt-6 font-default font-bold text-foreground leading-[0.92] tracking-[-0.03em] text-[clamp(2.75rem,7vw,6rem)] transition-all duration-[600ms] delay-[80ms] ease-[cubic-bezier(0.16,1,0.3,1)]", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"].join(" ")}>LAND CLEARING<br />in BURLINGTON, NC<br /></h1>

              {}
              <p className={["mt-8 max-w-xl font-default text-muted-foreground leading-[1.55] text-[clamp(1.05rem,1.4vw,1.25rem)] transition-all duration-[600ms] delay-[160ms] ease-[cubic-bezier(0.16,1,0.3,1)]", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"].join(" ")}>Transform your overgrown property into usable land with Chipper Mulching Co land clearing service.&nbsp; Our eco-friendly process clears brush and trees while returning nutrients directly to your soil.</p>

              {}
              <div className={["mt-10 flex flex-col sm:flex-row gap-4 transition-all duration-[600ms] delay-[240ms] ease-[cubic-bezier(0.16,1,0.3,1)]", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"].join(" ")}>
                <Button asChild className="rounded-lg bg-primary text-primary-foreground font-semibold px-7 py-3.5 h-auto text-sm transition-all duration-200 hover:bg-primary/90 hover:-translate-y-0.5 shadow-md">
                  <Link to="/ServicesPage">OUR SERVICES</Link>
                </Button>
                <Button asChild variant="outline" className="rounded-lg border border-secondary bg-transparent text-secondary font-semibold px-7 py-3.5 h-auto text-sm transition-colors duration-200 hover:border-primary hover:bg-accent-cream hover:text-primary">
                  <Link to="/ContactUsPage">SCHEDULE A SITE VISIT</Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="relative">
              {}
              <div className="absolute -top-4 -right-2 z-20 flex size-16 md:size-20 items-center justify-center rounded-lg border border-primary bg-card/80 backdrop-blur-sm shadow-lg">
                <div className="flex flex-col items-center">
                  <ShieldCheck className="size-7 md:size-8 text-primary" />
                  <span className="mt-0.5 font-mono text-[0.55rem] tracking-[0.12em] text-muted-foreground uppercase">
                    Insured
                  </span>
                </div>
              </div>

              {}
              <div ref={imageRef} className="relative overflow-hidden rounded-lg shadow-xl">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/forestry_mulcher_action.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/forestry_mulcher_action.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/forestry_mulcher_action.png-wvc-sizes" alt="Forestry mulching machine clearing land" loading="lazy" decoding="async" className="w-full h-auto object-cover will-change-transform" style={{
                transform: `translateY(${offset}px)`
              }} />
              </div>

              {}
              <div className="mt-4 overflow-hidden border-y border-dashed border-border py-3">
                <div className="flex w-max animate-[hype-marquee_40s_linear_infinite] gap-0 hover:[animation-play-state:paused]">
                  {tickerItems.map((item, i) => <div key={i} data-index={i} className="flex items-center font-mono text-[0.78rem] tracking-[0.08em] text-muted-foreground uppercase">
                      <span>{item}</span>
                      <span className="mx-4 text-primary">{"\u00B7"}</span>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes hype-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>;
}